import { memo, type SyntheticEvent, useCallback, useState } from 'react';

import { WorkspaceAgentPanelImage } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelImage';
import { WORKSPACE_AGENT_PANEL_ROUNDED_CORNERS_PREVIEW } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';
import {
  getWorkspaceAgentGuidedVariantCanvasColor,
  WORKSPACE_AGENT_GUIDED_VARIANT_CHECKERBOARD_STYLE,
  type WorkspaceAgentGuidedVariantColorOptionId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantColor';
import {
  getWorkspaceAgentRoundedCornersPreviewBorderRadius,
  type WorkspaceAgentRoundedCornersUnitId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentRoundedCorners';

type MeasuredPreviewSize = {
  height: number;
  url: string;
  width: number;
};

export type WorkspaceAgentGuidedVariantRoundedCornersPreviewProps = {
  cornerColorId: WorkspaceAgentGuidedVariantColorOptionId;
  customColor: string;
  imageAlt: string;
  previewHeight: number | null;
  previewImageUrl: string | null;
  previewWidth: number | null;
  radius: number;
  unit: WorkspaceAgentRoundedCornersUnitId;
};

export const WorkspaceAgentGuidedVariantRoundedCornersPreview = memo(
  ({
    cornerColorId,
    customColor,
    imageAlt,
    previewHeight,
    previewImageUrl,
    previewWidth,
    radius,
    unit,
  }: WorkspaceAgentGuidedVariantRoundedCornersPreviewProps) => {
    const [measuredSize, setMeasuredSize] = useState<MeasuredPreviewSize | null>(null);
    const canvasColor = getWorkspaceAgentGuidedVariantCanvasColor(cornerColorId, customColor);
    const measuredForCurrentImage = measuredSize?.url === previewImageUrl ? measuredSize : null;
    const borderRadius = getWorkspaceAgentRoundedCornersPreviewBorderRadius({
      height: previewHeight ?? measuredForCurrentImage?.height ?? null,
      radius,
      unit,
      width: previewWidth ?? measuredForCurrentImage?.width ?? null,
    });

    const handleImageLoad = useCallback(
      (event: SyntheticEvent<HTMLImageElement>) => {
        const { naturalHeight, naturalWidth } = event.currentTarget;

        if (!previewImageUrl || !naturalWidth || !naturalHeight) {
          return;
        }

        setMeasuredSize({ height: naturalHeight, url: previewImageUrl, width: naturalWidth });
      },
      [previewImageUrl],
    );

    return (
      <div
        className="size-20 shrink-0 border border-gray-6"
        data-border-radius={borderRadius}
        data-corner-color={canvasColor ?? 'transparent'}
        data-corner-color-id={cornerColorId}
        data-mat={canvasColor ? 'solid' : 'checkerboard'}
        data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_ROUNDED_CORNERS_PREVIEW"
        style={canvasColor ? { backgroundColor: canvasColor } : WORKSPACE_AGENT_GUIDED_VARIANT_CHECKERBOARD_STYLE}
      >
        {previewImageUrl ? (
          <div className="size-full overflow-hidden" style={{ borderRadius }}>
            <WorkspaceAgentPanelImage
              {...WORKSPACE_AGENT_PANEL_ROUNDED_CORNERS_PREVIEW}
              alt={imageAlt}
              className="size-full object-cover"
              onLoad={handleImageLoad}
              src={previewImageUrl}
            />
          </div>
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentGuidedVariantRoundedCornersPreview.displayName = 'WorkspaceAgentGuidedVariantRoundedCornersPreview';
